import React, { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import _get from "lodash/get";
import _cloneDeep from "lodash/cloneDeep";
import _set from "lodash/set";
import * as Preview from "@repoflow/components/preview";
import { useEditor } from "@repoflow/components/editor";
import { previewSrc } from "env";
import * as SectionQL from "queries/section";
import * as SiteQL from "queries/site";
import * as PageRender from "@common/render/page";
import { fetchBlogData } from "render/blog-data";

import { useEditorPage } from "../context";
import { editablePath } from "./path";

// The right-hand pane: the page as it would be served, live.
//
// This is the editor's end of the preview protocol — @repoflow/components/preview
// owns the iframe and the messages, and this owns what goes into it: the page
// currently in the form (not the saved one), the sections it references, and
// the parameters from the Editor tab.
export const Frame = ({ siteId, page, state }) => {
  const client = useApolloClient();
  const editor = useEditor();
  const { openElement } = useEditorPage();
  const { setAuto, setHighlight, selected, auto, highlight } = editor;

  // Compose the payload. Called each time the frame reloads and announces
  // itself, which is why it fetches rather than reading a prop: a section can
  // change while the editor is open, and the reload is when to notice.
  const onMounted = useCallback(
    async ({ send, setLoading, selected: current, position }) => {
      setLoading(true);

      const sectionIds = PageRender.referencedSectionIds(page);

      // In parallel, and one query per DISTINCT id — see the same fetch on the
      // page's preview screen for why neither `list` nor a loop is right here.
      const results = await Promise.all(
        sectionIds.map((sectionId) =>
          client
            .query({ query: SectionQL.get, variables: { siteId, sectionId } })
            .then((res) => [
              sectionId,
              _get(res, "data.viewer.account.sites.get.sections.get"),
            ])
            // One section that could not be read must not blank the whole
            // preview — it renders in place, where it stood.
            //
            // WHICH message, though, is the point of the marker. A removed
            // section answers with an error rather than a null, and so does a
            // dropped connection, an expired token and a revoked access; this
            // used to catch all of them into `null` and print "Section not
            // found", which sends somebody looking for a section that is still
            // there. See sectionUnavailable in @common/render/page.
            .catch((error) => [sectionId, PageRender.sectionUnavailable(error)])
        )
      );

      const sections = {};
      for (const [sectionId, section] of results) {
        if (section) {
          sections[sectionId] = section;
        }
      }

      // The site, for its configuration — the values and attributes a section
      // interpolates AND the shell those sections are composed into. Cache-first
      // — the default — so this is a read of what the site route already
      // fetched and not a round trip.
      //
      // It used to be the context half only, on the grounds that the frame does
      // not build a document. It does not, but it composes into one, and
      // sending the context without the shell made this pane show a site's
      // markup with none of its styling — total on a site whose look comes from
      // a linked stylesheet, since a classless framework styles `main` and
      // `header` by element. preview/mount applies both now, from the same
      // shell() and bodyOf() the served page uses. A preview that renders
      // differently from the thing it previews is worse than no preview.
      const siteResult = await client.query({
        query: SiteQL.get,
        variables: { siteId },
      });

      // The blog elements' data, for the same params the frame composes with —
      // the ones the author typed on the Editor tab — so a `/blog/:slug` page
      // previews the post whose slug they typed.
      const params = PageRender.editorParams(page);
      const data = await fetchBlogData(client, siteId, page, params);
      setLoading(false);

      send("mount", {
        page,
        sections,
        site: _get(siteResult, "data.viewer.account.sites.get"),
        params,
        data,
        selected: current,
        position,
      });
    },
    [client, siteId, page]
  );

  // Somebody double-clicked an element in the preview. Open it.
  // openElement switches to the Elements tab as well as opening the element,
  // which is what makes this work from the Page tab too.
  const onGoto = useCallback((index) => openElement(index), [openElement]);

  // Somebody typed into a `data-edit` node in the preview.
  //
  // The path is absolute into the page's own value object — the composer
  // rewrote it that way (see editorMarkup in @common/render/template) precisely so
  // that this end needs to know nothing about which element it came from.
  //
  // CHECKED, and against this page rather than against the format alone: this
  // is a postMessage handler and `_set` creates every key on the way down, so
  // an unchecked `datapath` is an arbitrary write into the object Save posts.
  // ./path says what a legitimate one looks like and why, and the section
  // editor's frame has had the equivalent since it was written. Ignored rather
  // than repaired when it does not match, which is what the section editor
  // does with one it does not recognise.
  //
  // cloneDeep before set, because lodash's `set` mutates: writing through the
  // form's current values would change them in place, so `isEqual` would find
  // nothing to save and the Save button would never light up.
  const onInput = useCallback(
    ({ datapath, value }) => {
      const path = editablePath(datapath, state.values.elements);

      if (!path) {
        return;
      }

      const mod = _cloneDeep(state.values);
      _set(mod, path, value);
      state.setValues(mod);
    },
    [state]
  );

  return (
    <Preview.Frame
      // Absolute, and since P142 usually CROSS-ORIGIN: the frame is the same
      // preview route served from the sites hostname, so a tenant's `<script>`
      // does not run with this application's origin. `previewSrc` falls back to
      // this origin where there is no distribution name to build one from.
      src={previewSrc()}
      watch={page}
      selected={selected}
      auto={auto}
      highlight={highlight}
      onAutoChange={setAuto}
      onHighlightChange={setHighlight}
      onMounted={onMounted}
      onGoto={onGoto}
      onInput={onInput}
    />
  );
};
